import { InlineEditorCancelButton, InlineEditorEditButton,InlineEditorLogic, InlineEditorLogic_EditMode, InlineEditorSaveButton, isReadonlyMode, useInlineEditor } from '@tableau/vizportalclient-common-components';
import { TextAreaWidget } from '@tableau/widgets';
import React from 'react';

import { CommonStyles } from '~common/react/CommonStyles';
import { ISanitizer } from '~commonInfra/utils/Sanitize';

import { Formatters,Messages } from '../../../codegen/Localize';
import { TextileHelper } from '../../../ts/helpers/TextileHelper';
import { IDescriptionItemInfo } from '../../../ts/models/resourceInfoParts';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';

export const InlineEditableDescriptionTestId: string = 'editable-description-readonly-mode';

export interface InlineEditableDescriptionOwnProps {
  readonly maxCharacterCount: number;
  readonly item: IDescriptionItemInfo;
  readonly isAdminOrOwner?: boolean;
  readonly descriptionPrompt?: string;
  readonly ariaLabel: string;
  readonly updateDescription: (description: string) => Promise<void>;
  readonly sanitizer: ISanitizer;
  readonly canEdit: boolean;
  readonly onEditChange?: (isEditing: boolean) => void;
}

export type InlineEditableDescriptionProps = InlineEditableDescriptionOwnProps;

export function InlineEditableDescription(props: InlineEditableDescriptionProps): JSX.Element {
  const [description, setDescription] = React.useState(props.item.description);

  const isValidDescription = React.useCallback(
    (description: string): boolean => {
      if (description) {
        return description.length >= 0 && description.length <= props.maxCharacterCount;
      }
      return true;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onSave = async (description: string): Promise<void> => {
    try {
      await props.updateDescription(description);
      props.onEditChange && props.onEditChange(false);
      setDescription(description);
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.unexpectedError()
      });
      throw e;
    }
  };

  const logic: InlineEditorLogic<string> = useInlineEditor<string>({
    editorStateWhenEnteringEditMode: description,
    canEdit: props.canEdit,
    onSave: onSave,
    onCancel: () => { props.onEditChange && props.onEditChange(false); },
    canSaveFn: isValidDescription,
    exitEditModeOnFailedSave: true,
  });

  const showDescription = props.isAdminOrOwner || description;
  const editableStyling: string = props.canEdit ? SpaceHeaderStyles.HeaderEditableInlineElement : '';

  if (isReadonlyMode(logic)) {
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return <>
      { showDescription &&
        <div
          data-tb-test-id={InlineEditableDescriptionTestId}
          className={`${SpaceHeaderStyles.InlineDescriptionReadonly} ${editableStyling}`}
          onClick={() => {
            props.canEdit && logic.enterEditMode();
            props.onEditChange && props.onEditChange(true);
          }}
        >
          { description && <span>{TextileHelper.getPlainTextFromTextile(description, props.sanitizer)}</span>}
          { !description && <span style={{color: CommonStyles.Colors.Grey112}}>{props.descriptionPrompt !== undefined ?
            props.descriptionPrompt : Messages.collectionDescriptionPrompt()}</span>}
          <span style={{paddingLeft: CommonStyles.Sizes.SmallSpace, alignSelf: 'flex-start'}}>
            <InlineEditorEditButton {...logic} editButtonLabel={Messages.editDescription2()} />
          </span>
        </div>
      }
    </>;
  } else {
    return <InlineEditableDescriptionEditMode editLogic={logic} maxCharacterCount={props.maxCharacterCount} ariaLabel={props.ariaLabel}/>;
  }
}

export interface InlineEditableDescriptionEditModeProps {
  readonly editLogic: InlineEditorLogic_EditMode<string>;
  readonly maxCharacterCount: number;
  readonly ariaLabel: string;
}

export function InlineEditableDescriptionEditMode(props: InlineEditableDescriptionEditModeProps): JSX.Element {
  const inputEl = React.useRef<HTMLTextAreaElement>(null);

  const getMessageForDescriptionTextArea = React.useCallback((value: string): string => {
    const remainingChars = value ? props.maxCharacterCount - value.length : props.maxCharacterCount;
    return Messages.remainingCharacters({
      COUNT: remainingChars,
      COUNT_LOC: Formatters.formatCount(remainingChars)
    });
  }, [props.maxCharacterCount]);

  const [message, setMessage] = React.useState(getMessageForDescriptionTextArea(props.editLogic.editorState));

  const onDescriptionChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      props.editLogic.setEditorStateWithinEditMode(value);
      setMessage(getMessageForDescriptionTextArea(value));
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.editLogic.setEditorStateWithinEditMode]
  );

  React.useEffect(() => {
    inputEl.current?.focus();
  }, []);

  return (
    <span data-tb-test-id={'editable-description-edit-mode'}
          className={SpaceHeaderStyles.InlineDescriptionEditResponsiveContainer}
          style={{ right: 0 }}>
      <TextAreaWidget
        testId='collection-description-edit'
        label=''
        aria-label={props.ariaLabel}
        maxLength={props.maxCharacterCount}
        onChange={onDescriptionChange}
        value={props.editLogic.editorState}
        message={message}
        className={SpaceHeaderStyles.HeaderInlineDescriptionTextArea}
        ref={inputEl}
      />
      <div className={SpaceHeaderStyles.ConfirmCancelButtons}>
        <InlineEditorCancelButton {...props.editLogic} />
        <InlineEditorSaveButton {...props.editLogic} />
      </div>
    </span>
  );
}
